import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import cloneDeep from 'lodash/cloneDeep'
import { rootActions } from '@app-store/slices'
import { RootState } from '@app-store/store'
import { COOKIE_KEYS, getCookie, LOCALSTORAGE_KEYS } from '@features/General'
import routes from '@utils/constants/routes'
import { NOTIFICATION_EVENTS, NOTIFICATION_STATUSES, NotificationRecordTypes } from '@utils/constants'

function useNotification(actionListName: string, list_size: number) {
    const [notifications, setNotifications] = useState([])
    const [eventType, setEventType] = useState('All')
    const dispatch = useDispatch()
    const {
        data: notificationList,
        pagination: { total }
    } = useSelector((state: RootState) => state.notification[actionListName])
    const { data: i18n } = useSelector((state: RootState) => state.localization.locals)

    const eventTypeOptions = [
        { value: 'All', label: i18n?.General?.All },
        { value: NOTIFICATION_EVENTS.NEW_REQUEST_MEETING, label: i18n?.TradeHub?.New_Meeting_Requests },
        { value: NOTIFICATION_EVENTS.ACCEPT_REQUEST_MEETING, label: i18n?.TradeHub?.Accepted_Meeting_Requests },
        { value: NOTIFICATION_EVENTS.REJECT_REQUEST_MEETING, label: i18n?.TradeHub?.Rejected_Meeting_Requests },
        { value: NOTIFICATION_EVENTS.RESCHEDULE_REQUEST_MEETING, label: i18n?.TradeHub?.Reschedule_Meeting_Requests },
        { value: NOTIFICATION_EVENTS.CANCEL_REQUEST_MEETING, label: i18n?.TradeHub?.Cancelled_Meeting_Requests },
        { value: NOTIFICATION_EVENTS.UPDATE_REQUEST_MEETING, label: i18n?.TradeHub?.Updated_Meeting_Requests },
        { value: NOTIFICATION_EVENTS.SCHEDULE_MEETING, label: i18n?.Meeting?.Schedule_Meeting },
        { value: NOTIFICATION_EVENTS.RESCHEDULE_MEETING, label: i18n?.Meeting?.['Reschedule Meeting'] },
        { value: NOTIFICATION_EVENTS.CANCEL_MEETING, label: i18n?.Meeting?.['Cancel Meeting'] }
    ]

    useEffect(() => {
        setNotifications(prev => {
            const next = [...prev, ...cloneDeep(notificationList)]
            return next
        })
    }, [notificationList])

    useEffect(() => {
        const params: any = {
            skip: 0,
            limit: list_size,
            needPopulate: true,
            populates: ['senderCompany', 'senderContact', 'meeting'],
            query: { type: 'System' }
        }
        if (eventType !== 'All') {
            params.query.trigger = eventType
        }
        dispatch(rootActions.notification[actionListName].onRequest(params))
        setNotifications([])
    }, [eventType])

    const handleMarkAsRead = id => {
        const payload = {
            notificationId: id,
            sagaCB: {
                onSuccess: () => {
                    setNotifications(prev => {
                        const next = [...prev]
                        const notification = next.find(item => item._id === id)
                        notification.status = NOTIFICATION_STATUSES.READ
                        return next
                    })
                }
            }
        }
        dispatch(rootActions.notification.markAsRead.onRequest(payload))
    }

    const handleShowMore = () => {
        const params: any = {
            skip: notifications.length,
            limit: list_size,
            needPopulate: true,
            populates: ['senderCompany', 'senderContact', 'meeting'],
            query: { type: 'System' }
        }
        if (eventType !== 'All') {
            params.query.trigger = eventType
        }
        dispatch(rootActions.notification[actionListName].onRequest(params))
    }

    const handleClickNotification = (e, record) => {
        e.preventDefault()
        handleMarkAsRead(record._id)
        let selectedEvent: any = {}
        if ([NotificationRecordTypes.PRODUCT, NotificationRecordTypes.SERVICE].includes(record?.relatedRecordType)) {
            const userProfile: any = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEYS.USERINFO))
            const eventId = getCookie(COOKIE_KEYS.ALA_EVENT)
            selectedEvent = eventId ? userProfile?.eventInfos.find(item => item.eventId === eventId) : null
        }
        if (record?.relatedRecordType === NotificationRecordTypes.MEETING_REQUEST) {
            dispatch(
                rootActions.meetingRequests.one.onRequest({
                    requestId: record.relatedRecordId,
                    sagaCB: {
                        onSuccess: requestRecord => {
                            if (
                                [
                                    NOTIFICATION_EVENTS.ACCEPT_REQUEST_MEETING,
                                    NOTIFICATION_EVENTS.REJECT_REQUEST_MEETING
                                ].includes(record.trigger)
                            ) {
                                dispatch(
                                    rootActions.ui.outgoingMeetingRequests.changeInfoModal({
                                        isOpen: true,
                                        requestInfo: requestRecord
                                    })
                                )
                            } else {
                                dispatch(
                                    rootActions.ui.incomingMeetingRequests.changeInfoModal({
                                        isOpen: true,
                                        requestInfo: requestRecord
                                    })
                                )
                            }
                        }
                    }
                })
            )
        } else if (record?.relatedRecordType === NotificationRecordTypes.COMPANY) {
            window.open(
                routes.directory.company_profile.profile._.replace(':id', record.relatedRecordId),
                '_blank',
                'noopener,noreferrer'
            )
        } else if (record?.relatedRecordType === NotificationRecordTypes.CONTACT) {
            window.open(
                routes.directory.contact_profile.details._.replace(':id', record.relatedRecordId),
                '_blank',
                'noopener,noreferrer'
            )
        } else if (record?.relatedRecordType === NotificationRecordTypes.MEETING) {
            window.open(
                routes.meetings.my_agenda.profile._.replace(':id', record.relatedRecordId),
                '_blank',
                'noopener,noreferrer'
            )
        } else if (record?.relatedRecordType === NotificationRecordTypes.PRODUCT) {
            dispatch(
                rootActions.products.view.setViewProductModal({ visible: true, productId: record.relatedRecordId })
            )
            window.open(
                routes.directory.company_profile.digital_shop._.replace(':id', selectedEvent.companyId),
                '_blank',
                'noopener,noreferrer'
            )
        } else if (record?.relatedRecordType === NotificationRecordTypes.SERVICE) {
            window.open(
                routes.directory.company_profile.services._.replace(':id', selectedEvent.companyId),
                '_blank',
                'noopener,noreferrer'
            )
        } else if (record?.relatedRecordType === NotificationRecordTypes.RFQ) {
            // window.open(
            //     routes.directory.company_profile.services._.replace(':id', selectedEvent.companyId),
            //     '_blank',
            //     'noopener,noreferrer'
            // )
        } else if (record?.relatedRecordType === NotificationRecordTypes.QUOTATION) {
            // window.open(
            //     routes.directory.company_profile.services._.replace(':id', selectedEvent.companyId),
            //     '_blank',
            //     'noopener,noreferrer'
            // )
        } else if (record?.relatedRecordType === NotificationRecordTypes.SHARED_LIST) {
            window.open(
                routes.matching.match_list.response._.replace(':id', selectedEvent._id),
                '_blank',
                'noopener,noreferrer'
            )
        }
    }

    return {
        eventTypeOptions,
        handleMarkAsRead,
        handleClickNotification,
        handleShowMore,
        notifications,
        eventType,
        setEventType,
        total,
        i18n
    }
}

export default useNotification
